import type { ChapterData } from "./content-types";

/**
 * CAPÍTULO 6 — "Tensión" (Central Eléctrica).
 * Mecánica: LAS CINCO REGLAS DE ORO (reutiliza el tipo "lockout").
 * Capítulo de la REVELACIÓN: Núñez firma las 'optimizaciones', Vega es inspector.
 */
export const CENTRAL: ChapterData = {
  id: "central",
  title: "Tensión",
  location: "central",
  secStart: 40,
  secGreat: 84,
  secOk: 68,
  secBad: 56,
  needTalk: 2,
  needHazards: 3,

  interactables: [
    { id: "cuadro", type: "machine", pos: { x: 7, y: 4 }, label: "Cuadro eléctrico", verb: "use", ref: "cuadro", radius: 1.8, color: 0x5a626c },
    { id: "cable", type: "machine", pos: { x: 7, y: 6 }, label: "Cable dañado", verb: "use", ref: "cable", radius: 1.5, color: 0x1e2228 },
    { id: "agua", type: "object", pos: { x: 5, y: 6 }, label: "Fuga de agua", verb: "inspect", ref: "agua", radius: 1.4, color: 0x6a86a0 },
    { id: "verificador", type: "object", pos: { x: 3, y: 4 }, label: "Verificador de tensión", verb: "inspect", ref: "verificador", radius: 1.4, color: 0xf0c400 },
    { id: "autorizacion", type: "document", pos: { x: 12, y: 5 }, label: "Orden de trabajo", verb: "read", ref: "autorizacion", radius: 1.4 },
    { id: "expediente", type: "document", pos: { x: 11, y: 7 }, label: "Expediente de optimización", verb: "read", ref: "expediente", radius: 1.4, color: 0xc9a06a },
    { id: "nando", type: "npc", pos: { x: 5, y: 8 }, label: "Nando", verb: "talk", ref: "nando", radius: 1.5, color: 0x2b3f6b },
    { id: "nunez", type: "npc", pos: { x: 9, y: 8 }, label: "Núñez", verb: "talk", ref: "nunez", radius: 1.5, color: 0x2f2f36 },
    { id: "vega", type: "npc", pos: { x: 13, y: 9 }, label: "Inspector Vega", verb: "talk", ref: "vega", radius: 1.5, color: 0x3a3a42 },
    { id: "taquilla", type: "locker", pos: { x: 2, y: 8 }, label: "Taquilla de EPIs", verb: "open", radius: 1.6 },
    { id: "salida", type: "door", pos: { x: 13, y: 10 }, label: "Salir a la ciudad", verb: "open", radius: 1.7 },
  ],

  hotspots: {
    cuadro: {
      label: "Cuadro eléctrico",
      text: "El <b>cuadro</b> abierto, con partes <b>en tensión al descubierto</b>. Nadie lo ha desconectado: sigue vivo. Antes de tocar nada hay que aplicar las <b>cinco reglas de oro</b>.",
      note: { section: "pistas", heading: "Cuadro en tensión y abierto", body: "Se intervino en el cuadro sin desconectarlo. Antes de tocar nada hay que aplicar las cinco reglas de oro." },
      hazardId: "h_cuadro",
      comment: "El cuadro, abierto y vivo. Meter la mano ahí sin cortar la corriente es de todo menos valiente.",
    },
    cable: {
      label: "Cable dañado",
      text: "Un <b>cable pelado</b> por el suelo, con el cobre a la vista, y un empalme hecho con cinta y prisas. Hay que <b>repararlo</b>... pero solo con la instalación segura.",
      note: { section: "pistas", heading: "Cable pelado y empalme casero", body: "Cable con el conductor al aire y un empalme improvisado. Riesgo de contacto directo." },
      hazardId: "h_cable",
      comment: "Un cable pelado y un empalme de cinta aislante. La electricidad no perdona las chapuzas.",
    },
    agua: {
      label: "Fuga de agua",
      text: "Una <b>fuga</b> gotea y forma un <b>charco justo bajo el cuadro</b>. Agua y electricidad: la peor pareja. Con el suelo mojado, tú cierras el circuito.",
      note: { section: "pistas", heading: "Agua junto al cuadro", body: "Humedad y charco bajo un cuadro en tensión. El agua conduce: multiplica el riesgo de electrocución." },
      hazardId: "h_agua",
      comment: "Un charco justo debajo del cuadro. Si tocas eso con los pies mojados, el circuito lo cierras tú.",
      signs: ["w_elec", "w_suelo", "p_agua"],
      assess: {
        identify: {
          q: "¿Qué riesgo añade el charco bajo el cuadro en tensión?",
          options: [
            { text: "Electrocución: el agua conduce y el suelo mojado cierra el circuito", ok: true },
            { text: "Riesgo biológico del agua estancada", ok: false },
            { text: "Riesgo de resbalón por el suelo mojado", ok: false },
          ],
          hint: "Agua y electricidad, la peor pareja. Piensa qué pasa si tocas el cuadro vivo con los pies sobre el charco.",
        },
        control: {
          q: "¿Cuál es la mejor medida, según la jerarquía preventiva?",
          options: [
            { text: "Reparar la fuga y secar la zona, y no intervenir hasta consignar el cuadro", tier: "eliminar", grade: "best", why: "Eliminar el agua y la tensión quita el riesgo de raíz: sin charco y con el cuadro consignado, no hay circuito que cerrar." },
            { text: "Trabajar con botas y guantes aislantes sobre el charco", tier: "epi", grade: "ok", why: "El EPI aislante protege, pero es el último recurso: no elimina el charco ni la tensión, que es lo que de verdad hay que quitar." },
            { text: "Poner unos cartones para pisar 'seco' y seguir", tier: "ninguna", grade: "bad", why: "Un cartón se empapa y no aísla: el suelo húmedo sigue conduciendo.", danger: "Un pie resbala al agua mientras tocas un borne: el circuito lo cierras tú." },
          ],
        },
        apply: {
          intro: "Nunca toques bajo el cuadro con tensión. Haz los pasos en orden.",
          steps: [
            "Cortar el suministro de agua de la fuga (en la válvula, no bajo el cuadro)",
            "Consignar el cuadro: cortar y bloquear con candado",
            "Verificar la ausencia de tensión antes de tocar nada",
            "Ya sin tensión, secar el charco y la zona bajo el cuadro",
          ],
          wrong: "Ese paso todavía no. Nunca se seca ni se toca bajo un cuadro con tensión: primero se corta el agua, se consigna y se verifica la ausencia de tensión; secar es lo último.",
          success: "Agua cortada, cuadro consignado y verificado sin tensión, y solo entonces zona seca: ya se puede trabajar sin cerrar el circuito con el cuerpo.",
        },
        note: { section: "expedientes", heading: "Agua y electricidad: eliminar antes que aislar", body: "La humedad multiplica el riesgo de electrocución. Primero se elimina el agua y se consigna la instalación; el EPI aislante es el último recurso." },
      },
    },
    verificador: {
      label: "Verificador de tensión",
      text: "El <b>verificador de ausencia de tensión</b>, colgado y <b>sin usar</b>. La regla que salva vidas: nunca se trabaja sin comprobar antes que de verdad no hay corriente.",
      note: { section: "pistas", heading: "No se verificó la ausencia de tensión", body: "Verificar ausencia de tensión es una de las cinco reglas de oro: sin ella, se trabaja a ciegas." },
      hazardId: "h_verif",
      comment: "El verificador, colgado sin estrenar. La regla de oro que más vidas salva, ignorada.",
      signs: ["i_verificador", "m_guantes"],
      instrument: {
        device: "i_verificador",
        button: "Verificar tensión",
        kind: "tester",
        state: "tension",
        danger: true,
        reading: "El verificador se enciende: TENSIÓN PRESENTE. El cuadro sigue vivo. Por eso nunca se toca hasta consignar (cortar, bloquear) y verificar de nuevo que ya no hay tensión.",
        note: { section: "pistas", heading: "Medición: tensión presente", body: "El verificador confirma que el cuadro sigue en tensión pese a estar 'abierto'. Verificar la ausencia de tensión es la regla de oro que evita la electrocución." },
      },
      assess: {
        identify: {
          q: "Has desconectado y bloqueado el cuadro. ¿Cuál es el riesgo que aún queda?",
          options: [
            { text: "Que siga habiendo tensión sin saberlo (otra fuente, un fallo) y tocarla", ok: true },
            { text: "Que la máquina arranque por sí sola", ok: false },
            { text: "Que la etiqueta de bloqueo se despegue", ok: false },
          ],
          hint: "Desconectar no es lo mismo que comprobar. Piensa cómo confirmas de verdad que ya no hay corriente antes de tocar.",
        },
        control: {
          q: "¿Cuál es la medida correcta antes de trabajar?",
          options: [
            { text: "Verificar la ausencia de tensión con el verificador antes de tocar", tier: "eliminar", grade: "best", why: "Confirmar que no hay tensión es la regla de oro que más vidas salva: se hace siempre con el equipo, tras desconectar y bloquear." },
            { text: "Fiarte de que el interruptor está bajado y empezar", tier: "ninguna", grade: "bad", why: "Un interruptor puede fallar o haber otra fuente: sin verificar, trabajas a ciegas.", danger: "El circuito seguía alimentado por otra línea; al tocar el borne, la descarga te alcanza." },
            { text: "Rozar un instante con el dorso de la mano para 'notarlo'", tier: "ninguna", grade: "bad", why: "Comprobar tensión con el cuerpo es jugarse la vida: para eso existe el verificador.", danger: "El dorso de la mano toca 400 V: la sacudida te lanza contra el cuadro." },
          ],
        },
        apply: {
          intro: "Verifica la ausencia de tensión como manda la regla de oro. Haz los pasos en orden.",
          steps: [
            "Probar el verificador en una fuente con tensión conocida",
            "Verificar la ausencia de tensión en los tres puntos del cuadro",
            "Volver a probar el verificador para descartar un fallo",
            "Solo entonces, poner a tierra y en cortocircuito",
          ],
          wrong: "Ese paso todavía no. El verificador se prueba antes y después de medir; sin eso, no puedes fiarte de la lectura.",
          success: "Ausencia de tensión verificada y verificador comprobado: ya no trabajas a ciegas.",
        },
        note: { section: "expedientes", heading: "Verificar ausencia de tensión", body: "Tercera regla de oro: tras desconectar y bloquear, se verifica con el equipo que no hay tensión. Nunca se comprueba con el cuerpo." },
      },
    },
    autorizacion: {
      label: "Orden de trabajo",
      text: "La <b>orden de trabajo</b>: quien abrió el cuadro <b>no era personal autorizado</b>. Los trabajos eléctricos los hace personal cualificado y con autorización.",
      note: { section: "expedientes", heading: "Trabajo sin autorización", body: "Intervino personal no cualificado ni autorizado. Los trabajos eléctricos requieren formación y autorización expresa." },
      giveItem: "orden",
      comment: "Abrió el cuadro alguien sin autorización. Esto no es 'apañar un enchufe': es riesgo eléctrico serio.",
    },
    expediente: {
      label: "Expediente de optimización",
      text: "La <b>carpeta completa</b> de 'medidas de optimización'. Cada recorte de prevención de la ciudad, con su número... y <b>una sola firma</b> al pie de todas: N. Núñez.",
      note: { section: "expedientes", heading: "El expediente completo", body: "Todas las 'optimizaciones' (01, 03, 05, 07, 09, 11) están en una misma carpeta, firmadas por la misma persona." },
      comment: "Aquí está todo. Todos los recortes, todos los números... y una firma que se repite. Ya te tengo.",
    },
  },

  npcs: {
    nando: {
      name: "Nando",
      role: "Electricista autorizado",
      portrait: "nando",
      note: { section: "personas", heading: "Nando", body: "Electricista autorizado. Avisó de que el cuadro no estaba consignado. Le recortaron el mantenimiento." },
      dialogue: [
        { speaker: "npc", text: "¡Quieto! No toques nada. Ese cuadro no está consignado, sigue con tensión. Un paso más y te quedas ahí pegado." },
        { speaker: "epi", text: "Tranquilo, no me acerco. ¿Cómo ha acabado alguien metiendo mano en un cuadro vivo?" },
        { choices: [ { text: "¿Qué debería haberse hecho antes de abrirlo?", go: 3 }, { text: "¿Por qué mandaron a alguien sin autorización?", go: 4 } ] },
        { speaker: "npc", text: "Las cinco reglas de oro, en orden y sin saltarse ninguna: desconectar, bloquear para que nadie reconecte, verificar que no hay tensión, poner a tierra y señalizar. Solo entonces se toca. Aquí no hicieron ni la primera.", flag: "nando_aviso", note: { section: "pistas", heading: "Las cinco reglas de oro, ignoradas", body: "Nando: antes de intervenir hay que aplicar las cinco reglas de oro (desconectar, bloquear, verificar, poner a tierra, señalizar). No se aplicó ninguna." }, end: true },
        { speaker: "npc", text: "Porque recortaron el mantenimiento y mi figura de autorizado 'para ahorrar'. Cuando saltó la avería, no había a quién llamar, así que mandaron al primero que pasaba. Sin cortar, sin bloquear, sin verificar. Un cuadro no distingue entre valiente y temerario.", flag: "nando_aviso", note: { section: "pistas", heading: "Mantenimiento eléctrico recortado", body: "La 'optimización' recortó el mantenimiento y el electricista autorizado. Al no haber personal cualificado, intervino alguien sin autorización ni consignación." }, end: true },
      ],
    },
    nunez: {
      name: "Núñez",
      role: "Dirección de Costes",
      portrait: "nunez",
      note: { section: "personas", heading: "N. Núñez", body: "Director de Costes. La firma de TODAS las 'optimizaciones'. Recortó prevención en toda la ciudad por ahorro." },
      dialogue: [
        { speaker: "npc", text: "¿Y usted quién es? Aquí solo optimizamos costes. Todo lo que ve son decisiones de eficiencia, perfectamente firmadas." },
        { speaker: "epi", text: "Firmadas por usted. Sillas, resguardos, extracción, mantenimiento... todo lo que protegía a alguien." },
        { choices: [ { text: "¿Sabe lo que ha provocado con esos recortes?", go: 3 }, { text: "Esto no es 'eficiencia'. Es poner en peligro a la gente.", go: 4 } ] },
        { speaker: "npc", text: "Yo veo números, no personas. Una barandilla es un gasto; una silla, otro; un electricista, otro más. Si nada malo pasa, ¿para qué pagarlo? Es solo optimizar.", flag: "nunez_confiesa", note: { section: "pistas", heading: "El motivo de Núñez", body: "Núñez admite que recortó cada protección por ahorro, viendo solo el coste y no el riesgo." }, end: true },
        { speaker: "npc", text: "'Peligro' es una palabra muy grande. Yo no he tirado a nadie de un andamio. Solo he quitado lo que costaba dinero. Que nadie se haya matado me da la razón, ¿no?", flag: "nunez_confiesa", note: { section: "pistas", heading: "El motivo de Núñez", body: "Núñez cree que, como aún no ha muerto nadie, sus recortes están justificados. La prevención se demuestra en los accidentes que NO ocurren." }, end: true },
      ],
    },
    vega: {
      name: "Inspector Vega",
      role: "Inspección de Trabajo",
      portrait: "vega",
      note: { section: "personas", heading: "Inspector Vega", body: "Por fin se identifica: Inspección de Trabajo. Llevaba meses siguiendo el rastro de las 'optimizaciones'." },
      dialogue: [
        { speaker: "npc", text: "Cero uno. La primera de todas... y la última que te faltaba. Toma: con esta, la lista queda entera, EPI." },
        { speaker: "epi", text: "Todo este tiempo... ¿quién es usted en realidad?" },
        { speaker: "npc", text: "Inspector Vega, Inspección de Trabajo. Sabía que alguien recortaba prevención por toda la ciudad, pero necesitaba pruebas: los números, la carpeta, la firma. Tú las has reunido. Con esto, las 'optimizaciones' se acabaron.", flag: "vega_revelado", note: { section: "pistas", heading: "Quién es Vega", body: "Vega es inspector de Trabajo. Con el expediente completo y la firma de Núñez, puede parar las 'optimizaciones'." }, end: true },
      ],
    },
  },

  hazards: [
    { id: "h_cuadro", category: "Eléctrico: cuadro en tensión", severity: 3, discoverable: true, requiredAction: "Consignar con las cinco reglas de oro" },
    { id: "h_agua", category: "Eléctrico + agua", severity: 3, discoverable: true, requiredAction: "Secar y aislar; nunca agua cerca de tensión" },
    { id: "h_cable", category: "Contacto directo", severity: 3, discoverable: true, requiredAction: "Reparar por personal autorizado" },
    { id: "h_verif", category: "Sin verificación", severity: 2, discoverable: true, requiredAction: "Verificar ausencia de tensión" },
  ],

  mission: {
    id: "cap6_tension",
    title: "Tensión",
    location: "central",
    objectives: [
      { id: "talk", type: "talk_workers", label: "Habla con quien anda por la central", target: "2" },
      { id: "hazards", type: "discover_hazards", label: "Detecta los riesgos eléctricos", target: "3" },
      { id: "extra", type: "extra", label: "Aplica las cinco reglas de oro", hidden: true },
      { id: "equip", type: "equip", label: "Ponte el equipo aislante", hidden: true },
      { id: "report", type: "report", label: "Repara la instalación con seguridad", hidden: true },
    ],
  },

  epi: {
    needed: ["guantes", "botas", "gafas"],
    optional: ["casco"],
    overkill: ["arnes", "mascarilla", "auditiva", "chaleco"],
  },

  extra: {
    triggerId: "cuadro",
    type: "lockout",
    signs: ["w_elec", "i_verificador", "m_guantes"],
    label: "Aplicar las cinco reglas de oro",
    steps: [
      { id: "desconectar", text: "Desconectar: abrir y cortar todas las fuentes de tensión." },
      { id: "bloquear", text: "Prevenir la realimentación: bloquear con candado y enclavar." },
      { id: "verificar", text: "Verificar la ausencia de tensión con el equipo adecuado." },
      { id: "tierra", text: "Poner a tierra y en cortocircuito." },
      { id: "senalizar", text: "Señalizar y delimitar la zona de trabajo." },
    ],
    wrong: "Te has saltado el orden de las cinco reglas. Y lo más importante: <b>no se toca nada hasta VERIFICAR</b> que no hay tensión. Empieza por desconectar todas las fuentes.",
    success: "Las cinco reglas de oro, en orden: desconectado, bloqueado, verificada la ausencia de tensión, puesto a tierra y señalizado. Ahora el cuadro está muerto de verdad y se puede trabajar sin jugarse la vida.",
    note: { section: "expedientes", heading: "Las cinco reglas de oro", body: "1) Desconectar. 2) Prevenir la realimentación (bloquear). 3) Verificar ausencia de tensión. 4) Poner a tierra. 5) Señalizar. En ese orden, siempre." },
  },

  consequence: {
    title: "Vuelves al cuadro",
    lines: {
      guantes: "Sin guantes aislantes, un roce con un borne te habría dado un calambre serio.",
      botas: "Sin calzado aislante y con el suelo húmedo, el circuito lo cierras tú.",
      gafas: "Sin pantalla ni gafas, un arco eléctrico puede dañarte la vista en un instante.",
    },
    great: "Con el cuadro consignado por las cinco reglas y el equipo aislante puesto, la reparación es segura.",
    greatOver: "Arnés y mascarilla aquí no pintan nada. 'Ni que fuera a escalar la torre de alta tensión.'",
    ok: "Sale, pero con algún roce que, sabiendo lo que investigabas, no tocaba.",
    bad: "Meterse en un cuadro sin equipo aislante, y con humedad, es justo lo que casi acaba mal.",
  },

  workId: "cable",
  reward: "tarjeta01",
  resolveNotes: [
    { section: "pistas", heading: "Tarjeta 01 · lista completa", body: "La primera de todas. Lista cerrada: 01, 03, 05, 07, 09, 11. Todas firmadas por N. Núñez (Dirección de Costes)." },
    { section: "expedientes", heading: "Caso: Tensión", body: "Riesgo eléctrico por cuadro sin consignar, personal no autorizado y agua. Resuelto con las cinco reglas de oro y EPI aislante. Revelado: Núñez firmó todas las 'optimizaciones'; Vega es inspector de Trabajo." },
  ],
  resolveText:
    `<p>Con el cuadro <b>consignado por las cinco reglas</b> y el equipo aislante puesto, reparas el cable con seguridad. El cuadro queda muerto de verdad.</p>
     <div class="epi-callout">La lista, completa: <b>01, 03, 05, 07, 09, 11</b>. Seis recortes, una sola firma: <b>N. Núñez</b>.<br>Vega se quita la careta: “Inspección de Trabajo. Con este expediente, las 'optimizaciones' se acaban hoy.”</div>`,
};
